// Devices.jsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UserDetails } from '../../../../services/oprations/User_Bussiness_Logic';
import {
  Smartphone, CheckCircle, XCircle, Clock, Wifi, WifiOff,
  MapPin, Cpu, Hash, User, Calendar, Shield, Activity,
  ChevronDown, ChevronUp, X
} from 'lucide-react';
import useSocket from '../../../../hooks/useSocket';

// ── helpers ──────────────────────────────────────────────────────────────────

const formatDate = (ds) => {
  if (!ds) return 'N/A';
  return new Date(ds).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const formatDateTime = (ds) => {
  if (!ds) return 'N/A';
  return new Date(ds).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const timeAgo = (ds) => {
  if (!ds) return 'N/A';
  const diff = Date.now() - new Date(ds).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

// ── Toast ─────────────────────────────────────────────────────────────────────

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-2xl backdrop-blur-sm
      ${type === 'success'
        ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
        : 'bg-gradient-to-r from-red-500 to-rose-500'
      }`}>
      <div className="text-sm font-semibold text-white">{message}</div>
      <button onClick={onClose} className="transition text-white/70 hover:text-white">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// ── Status badges ─────────────────────────────────────────────────────────────

const EnrollmentBadge = ({ status }) => {
  if (status === 'ACTIVE')
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30">
        <CheckCircle size={11} /> Active
      </span>
    );
  if (status === 'INACTIVE')
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400 border border-red-200 dark:border-red-500/30">
        <XCircle size={11} /> Inactive
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 border border-gray-200 dark:border-gray-600">
      <Clock size={11} /> {status || 'Unknown'}
    </span>
  );
};

const HeartbeatBadge = ({ status }) => {
  if (status === 'HEARTBEAT' || status === 'ONLINE')
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-blue-100/20 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30">
        <Activity size={11} className="animate-pulse" /> Online
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
      <WifiOff size={11} /> Offline
    </span>
  );
};

// ── Shared detail tile ────────────────────────────────────────────────────────

const DeviceDetail = ({ label, value, icon: Icon }) => (
  <div className="flex items-start gap-2 px-3 py-2 border border-gray-100 rounded-lg bg-gray-50 dark:bg-gray-800/60 dark:border-gray-700">
    {Icon && <Icon size={13} className="text-blue-500 dark:text-blue-400 mt-0.5 shrink-0" />}
    <div>
      <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">{label}</p>
      <p className="text-xs font-medium text-gray-800 break-all dark:text-gray-200">{value || 'N/A'}</p>
    </div>
  </div>
);

// ── Expanded details grid (shared between card & table) ───────────────────────

const ExpandedDeviceDetails = ({ dev, entry }) => (
  <>
    <div className="mb-3">
      <p className="flex items-center gap-2 text-xs font-semibold tracking-wider text-blue-600 uppercase dark:text-blue-400">
        <Shield size={12} /> Full Device Details
      </p>
    </div>
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      <DeviceDetail label="Device ID"          value={dev._id}                          icon={Hash} />
      <DeviceDetail label="Device Name"        value={dev.device_name}                  icon={Smartphone} />
      <DeviceDetail label="Device Code"        value={dev.device_code}                  icon={Hash} />
      <DeviceDetail label="Firmware"           value={dev.device_firmware_version}      icon={Cpu} />
      <DeviceDetail label="Location"           value={dev.device_location}              icon={MapPin} />
      <DeviceDetail label="Enrolled By"        value={dev.createdBy}                    icon={User} />
      <DeviceDetail label="Network Status"     value={dev.last_status}                  icon={Wifi} />
      <DeviceDetail label="Users Enrolled"     value={dev.users_count}                  icon={User} />
      <DeviceDetail label="Device Created"     value={formatDateTime(dev.createdAt)}    icon={Calendar} />
      <DeviceDetail label="Last Updated"       value={formatDateTime(dev.updatedAt)}    icon={Clock} />
      <DeviceDetail label="Last Seen"          value={formatDateTime(dev.device_last_seen)} icon={Activity} />
      <DeviceDetail label="User Enrollment ID" value={entry.enrollment_id}              icon={Hash} />
      <DeviceDetail label="Enrollment Status"  value={entry.enrollment_status}          icon={Shield} />
      <DeviceDetail label="Enrolled At"        value={formatDateTime(entry.enrollment_at)} icon={Calendar} />
      <DeviceDetail label="Org ID"             value={dev.org_id}                       icon={Shield} />
    </div>
  </>
);

// ── Mobile card ───────────────────────────────────────────────────────────────

const DeviceCard = ({ entry, index, onToggleExpand, isExpanded }) => {
  const dev = entry.device || {};
  const rowId = entry._id || index;

  return (
    <div className="overflow-hidden transition-all duration-300 bg-white border border-gray-200 shadow-sm rounded-xl dark:bg-gray-900 dark:border-gray-800">
      {/* Header */}
      <div className="p-4 cursor-pointer" onClick={() => onToggleExpand(rowId)}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center min-w-0 gap-2">
            <div className="p-1.5 rounded-lg bg-blue-100/20 dark:bg-blue-500/20 shrink-0">
              <Smartphone size={14} className="text-blue-500 dark:text-blue-400" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 truncate dark:text-white">
              {dev.device_name || 'Unnamed Device'}
            </h3>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onToggleExpand(rowId); }}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shrink-0"
          >
            {isExpanded
              ? <ChevronUp size={16} className="text-blue-500" />
              : <ChevronDown size={16} className="text-gray-400" />}
          </button>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <EnrollmentBadge status={entry.enrollment_status} />
          <HeartbeatBadge status={dev.last_status} />
        </div>

        {/* Quick info grid */}
        <div className="grid grid-cols-2 pt-3 mt-3 border-t border-gray-100 gap-x-4 gap-y-2 dark:border-gray-800">
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Device Code</p>
            <code className="font-mono text-xs text-gray-700 dark:text-gray-300">{dev.device_code || '—'}</code>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Location</p>
            <p className="text-xs text-gray-700 truncate dark:text-gray-300">{dev.device_location || '—'}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Firmware</p>
            <p className="text-xs text-gray-700 dark:text-gray-300">{dev.device_firmware_version || '—'}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Last Seen</p>
            <p className="text-xs text-gray-700 dark:text-gray-300">{timeAgo(dev.device_last_seen)}</p>
          </div>
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="px-4 pt-1 pb-4 border-t border-gray-100 bg-blue-100/20 dark:border-gray-800 dark:bg-blue-500/5">
          <ExpandedDeviceDetails dev={dev} entry={entry} />
        </div>
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const Devices = () => {
  const dispatch = useDispatch();
  const socket = useSocket();
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [expandedRows, setExpandedRows] = useState({});

  const { userDetails } = useSelector((state) => state.employeeAuth?.data || state.employeeAuth || {});
  const token = localStorage.getItem('token');

  const devices = userDetails?.devices || [];
  const activeCount  = devices.filter(d => d.enrollment_status === 'ACTIVE').length;
  const onlineCount  = devices.filter(d => ['HEARTBEAT', 'ONLINE'].includes(d.device?.last_status)).length;

  useEffect(() => {
    if (!socket) return;
    socket.on('Device_Status', () => dispatch(UserDetails(token)));
    return () => socket.off('Device_Status');
  }, [socket, dispatch, token]);

  const toggleRow = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));

  const statCards = [
    { label: 'Total Devices', value: devices.length,               color: 'text-gray-800 dark:text-white' },
    { label: 'Online Now',    value: onlineCount,                  color: 'text-blue-400 dark:text-blue-400' },
    { label: 'Offline Now',   value: devices.length - onlineCount, color: 'text-red-600 dark:text-red-400'},
    { label: 'Active In',     value: activeCount,                  color: 'text-yellow-400 dark:text-emerald-400' },
    // { label: 'Inactive',      value: devices.length - activeCount, color: 'text-red-600 dark:text-red-400' },
  ];

  const emptyState = (colSpan) => (
    <div className={colSpan ? undefined : undefined}>
      <div className="flex flex-col items-center gap-3 py-16 text-gray-400">
        <div className="p-4 bg-gray-100 rounded-full dark:bg-gray-800">
          <Smartphone size={28} className="opacity-40" />
        </div>
        <p className="text-sm font-medium">No devices enrolled</p>
        <p className="text-xs text-gray-400">Your account has no enrolled devices yet.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen transition-colors duration-300 bg-gray-50 dark:bg-black">
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'success' })}
        />
      )}

      <div className="p-4 mx-auto space-y-5 sm:p-6 md:p-8 max-w-7xl">

        {/* ── Header ── */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 sm:text-xl dark:text-white">Enrolled Devices</h1>
            <p className="mt-0.5 text-xs text-gray-500 sm:text-sm dark:text-gray-400">
              All devices linked to your account
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-xs text-gray-500 w-fit">
            <Wifi size={13} className="text-blue-500" />
            <span>{onlineCount} device{onlineCount !== 1 ? 's' : ''} online</span>
          </div>
        </div>

        {/* ── Stat cards ── */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {statCards.map((card, i) => (
            <div key={i} className="flex items-center justify-between p-3 transition-all duration-300 bg-white border border-gray-200 dark:bg-gray-900 dark:border-gray-800 rounded-xl hover:shadow-md sm:p-4">
              <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">{card.label}</p>
              <p className={`text-xs font-bold sm:text-xl ${card.color}`}>{card.value}</p>
            </div>
          ))}
        </div>

        {/* ── Mobile card list (visible below md) ── */}
        <div className="space-y-3 md:hidden">
          <div className="flex items-center justify-between px-1">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">Device Registry</span>
            <span className="text-xs text-gray-500">{devices.length} record{devices.length !== 1 ? 's' : ''}</span>
          </div>

          {devices.length > 0
            ? devices.map((entry, idx) => {
                const rowId = entry._id || idx;
                return (
                  <DeviceCard
                    key={rowId}
                    entry={entry}
                    index={idx}
                    onToggleExpand={toggleRow}
                    isExpanded={!!expandedRows[rowId]}
                  />
                );
              })
            : emptyState()}
        </div>

        {/* ── Desktop table (visible md+) ── */}
        <div className="hidden overflow-hidden transition-all duration-300 bg-white border border-gray-200 md:block dark:bg-gray-900 dark:border-gray-800 rounded-xl">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 border-b border-gray-100 dark:border-gray-800 sm:px-6">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">Device Registry</span>
            <span className="text-xs text-gray-500">{devices.length} record{devices.length !== 1 ? 's' : ''}</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  {['Device Name', 'Device Code', 'Location', 'Firmware', 'Enrolled By',
                    'Enrollment ID', 'Enrollment Date', 'Network Status', 'Details'].map((h) => (
                    <th key={h} className="px-3 py-3 text-xs font-medium text-left text-gray-500 uppercase whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {devices.length > 0 ? (
                  devices.map((entry, idx) => {
                    const dev    = entry.device || {};
                    const rowId  = entry._id || idx;
                    const isExp  = !!expandedRows[rowId];

                    return (
                      <React.Fragment key={rowId}>
                        <tr className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/40">
                          {/* Device Name */}
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2">
                              <div className="p-1.5 rounded-lg bg-blue-100/20 dark:bg-blue-500/20 shrink-0">
                                <Smartphone size={14} className="text-blue-500 dark:text-blue-400" />
                              </div>
                              <span className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                                {dev.device_name || '—'}
                              </span>
                            </div>
                          </td>

                          {/* Device Code */}
                          <td className="px-3 py-3">
                            <code className="text-xs font-mono px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                              {dev.device_code || '—'}
                            </code>
                          </td>

                          {/* Location */}
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                              <MapPin size={12} className="text-gray-400 shrink-0" />
                              {dev.device_location || '—'}
                            </div>
                          </td>

                          {/* Firmware */}
                          <td className="px-3 py-3">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400 border border-purple-100 dark:border-purple-500/20">
                              <Cpu size={10} />
                              {dev.device_firmware_version || '—'}
                            </span>
                          </td>

                          {/* Enrolled By */}
                          <td className="px-3 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <User size={12} className="text-gray-400 shrink-0" />
                              {dev.createdBy || '—'}
                            </div>
                          </td>

                          {/* Enrollment ID */}
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white">
                              <Hash size={12} className="text-blue-400 shrink-0" />
                              {entry.enrollment_id || '—'}
                            </div>
                          </td>

                          {/* Enrollment Date */}
                          <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <Calendar size={12} className="text-gray-400 shrink-0" />
                              {formatDate(entry.enrollment_at)}
                            </div>
                          </td>

                          {/* Network Status */}
                          <td className="px-3 py-3">
                            <HeartbeatBadge status={dev.last_status} />
                          </td>

                          {/* Expand toggle */}
                          <td className="px-3 py-3">
                            <button
                              onClick={() => toggleRow(rowId)}
                              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                              {isExp
                                ? <ChevronUp size={16} className="text-blue-500" />
                                : <ChevronDown size={16} className="text-gray-400" />}
                            </button>
                          </td>
                        </tr>

                        {/* Expanded row */}
                        {isExp && (
                          <tr className="bg-blue-100/20 dark:bg-blue-500/5">
                            <td colSpan={9} className="px-6 py-5">
                              <ExpandedDeviceDetails dev={dev} entry={entry} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={9}>
                      {emptyState()}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Devices;